// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { withStyles, Grid } from '@material-ui/core';
import { bool, func, shape, string } from 'prop-types';
import { replace } from 'lodash';

import {
  AlertDialog,
  AlertMessages,
  HelpContainer,
  Loading,
} from '../components';
import { BreadcrumbsForPages } from '../pageComponents';
import { API_DETAILS, ORGS_DETAILS,
  PROXIES_DETAILS, ADMIN_SETTINGS_AUTH_DETAILS,
} from '../pageComponents/BreadcrumbsForPages/constants';
import { getI18n } from '../utils/intl';
import styles from './edit.styles';

export const Edit = (props) => {
  const {
    classes,
    editPageId,
    dialogId,
    isDialogOpen,
    onDialogClose,
    dialogSubmitText,
    onDialogSubmit,
    isLoading,
    leftSidebarId,
    mainContentId,
    notificationId,
    notificationStatus,
    setNotificationStatus,
    notificationMessage,
    setNotificationMessage,
    tabsContainer,
    pageHeaderRef,
    pageHeaderTitle,
    pageContent,
    rightSidebarId,
    helpItems,
    hideRightSidebar = true,
    useOverlayLoader = false,
    pathName,
  } = props;

  const intl = getI18n(useIntl());
  const [breadcrubItems, setBreadcrumbItems] = useState([]);

  useEffect(() => {
    const pathNames = pathName && pathName.split('/').filter(x => x) || [];
    if(pathNames[0] === 'publish' && pathNames[1] === 'apis') {
      if(pathNames[2] === 'edit') {
        pathNames[4] ? setBreadcrumbItems([
            ...API_DETAILS, ...[
              { isActive: false, link: '', linkName: pageHeaderTitle },
              { isActive: false, link: '', linkName: replace(pathNames[4], '-', ' ') },
            ]])
          :
          setBreadcrumbItems([
            ...API_DETAILS, ...[
              { isActive: false, link: '', linkName: pageHeaderTitle },
            ]]);
      } else if(pathNames[2] === 'add') {
        setBreadcrumbItems([
          ...API_DETAILS, ...[
            { isActive: false, link: '', linkName: pageHeaderTitle },
          ]]);
      }
    }
    if(pathNames[0] === 'publish' && pathNames[1] === 'api-proxy') {
      if(pathNames[2] === 'edit') {
        pathNames[4] ? setBreadcrumbItems([
            ...PROXIES_DETAILS, ...[
              { isActive: false, link: '', linkName: pageHeaderTitle },
              { isActive: false, link: '', linkName: replace(pathNames[4], '-', ' ') },
            ]])
          :
          setBreadcrumbItems([
            ...PROXIES_DETAILS, ...[
              { isActive: false, link: '', linkName: pageHeaderTitle },
            ]]);
      } else if(pathNames[2] === 'add') {
        setBreadcrumbItems([
          ...PROXIES_DETAILS, ...[
            { isActive: false, link: '', linkName: pageHeaderTitle },
          ]]);
      }
    }
    if(pathNames[0] === 'admin' && pathNames[1] === 'console' && pathNames[2] === 'organizations') {
      if(pathNames[3] === 'add') {
        setBreadcrumbItems([
          ...ORGS_DETAILS, ...[
            { isActive: false, link: '', linkName: pageHeaderTitle },
          ]]);
      }
    }
    if(pathNames[0] === 'publish' && pathNames[1] === 'settings' && pathNames[2] === 'authentication') {
      if(pathNames[3] === 'edit') {
        pathNames[5] ?
          setBreadcrumbItems([
            ...ADMIN_SETTINGS_AUTH_DETAILS, ...[
              { isActive: false, link: '', linkName: pathNames[3] },
              { isActive: false, link: '', linkName: replace(pathNames[5], '-', ' ') },
            ]])
          : setBreadcrumbItems([
            ...ADMIN_SETTINGS_AUTH_DETAILS, ...[
              { isActive: false, link: '', linkName: pathNames[3] },
            ]]);
      }
      if(pathNames[3] === 'add') {
        pathNames[4] ?
          setBreadcrumbItems([
            ...ADMIN_SETTINGS_AUTH_DETAILS, ...[
              { isActive: false, link: '', linkName: pathNames[3] },
              { isActive: false, link: '', linkName: replace(pathNames[4], '-', ' ') },
            ]])
          : setBreadcrumbItems([
            ...ADMIN_SETTINGS_AUTH_DETAILS, ...[
              { isActive: false, link: '', linkName: pathNames[3] },
              { isActive: false, link: '', linkName: intl.getI18nFormattedMessage('label.auth.basic.details') },
            ]]);
      }
    }
  }, [pathName, pageHeaderTitle]);

  return (
    <div
      className={classes.content}
      id={editPageId}
      data-apim-test={editPageId}
    >
      <AlertDialog
        isOpen={isDialogOpen}
        title={intl.getI18nMessage('label.dialog.unsaved.changes.title')}
        description={intl.getI18nMessage('label.dialog.unsaved.changes.description')}
        submitText={dialogSubmitText}
        cancelText={intl.getI18nMessage('label.dialog.unsaved.changes.cancel')}
        onClose={onDialogClose}
        onSubmit={onDialogSubmit}
        onCancel={onDialogClose}
        dialogId={dialogId}
      />
      { (isLoading && !useOverlayLoader) ?
        <Loading />
        :
        <>
          {isLoading && <Loading pageLoader />}
          <Grid container className={classes.gridContainer}>
            <Grid
              md={2} item
              className={classes.leftSidebar}
              id={leftSidebarId} data-apim-test={leftSidebarId}
            >
              {tabsContainer}
            </Grid>
            <Grid
              md={hideRightSidebar ? 7 : 10} item
              className={classes.mainContent}
              id={mainContentId} data-apim-test={mainContentId}
            >
              <div ref={pageHeaderRef}>
                <BreadcrumbsForPages pageContainerClass={classes.pageBreadCrumbClass}
                  pathName={pathName} items={breadcrubItems} />
              </div>
            {notificationMessage &&
              <AlertMessages
                id={notificationId} data-apim-test={notificationId}
                variant={notificationStatus}
                message={notificationMessage}
                onClose={() => {
                  setNotificationMessage('');
                  setNotificationStatus('');
                }}
              />
            }
            <div className={classes.pageHeaderTitle} ref={pageHeaderRef}>{pageHeaderTitle}</div>
            <Fragment>{pageContent}</Fragment>
            </Grid>
            {hideRightSidebar &&
              <Grid
                md={3} item
                className={classes.rightSidebar}
                id={rightSidebarId} data-apim-test={rightSidebarId}
              >
                <HelpContainer helpItems={helpItems} />
              </Grid>
            }
            </Grid>
        </>
      }
    </div>
  );
};

Edit.propTypes = {
  classes: shape({}),
  editPageId: string,
  dialogId: string,
  isDialogOpen: bool,
  onDialogClose: func,
  dialogSubmitText: string,
  onDialogSubmit: func,
  isLoading: bool,
  leftSidebarId: string,
  mainContentId: string,
  notificationId: string,
  notificationStatus: string,
  setNotificationStatus: func,
  notificationMessage: string,
  setNotificationMessage: func,
  tabsContainer: shape({}),
  pageHeaderRef: shape({}),
  pageHeaderTitle: string,
  pageContent: shape({}),
  rightSidebarId: string,
  helpItems: shape({}),
  hideRightSidebar: bool,
  useOverlayLoader: bool,
  pathName: string,
};

export default compose(
  withStyles(styles),
)(Edit);

